import type { EndClient, Website } from '@lensora/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '@lensora/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Globe, Mail, Pencil, Plus, UserPlus, X } from 'lucide-react'
import * as React from 'react'

import { useClients } from '@/hooks/use-clients'
import { api, ApiError } from '@/lib/api'

// ─── Types ──────────────────────────────────────────────────────

type ClientContact = { id: string; email: string | null; fullName: string | null }
type ClientWithContacts = EndClient & { contacts: ClientContact[]; websites: Website[] }

// ─── Data hooks ────────────────────────────────────────────────

function useClient(id: string | null) {
  return useQuery({
    queryKey: ['clients', id],
    queryFn: () => api.get<ClientWithContacts>(`/api/admin/clients/${id}`),
    enabled: !!id,
  })
}

function useCreateClientMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; contactEmail?: string; websiteUrl?: string; notes?: string }) =>
      api.post<EndClient>('/api/admin/clients', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      // Creating a client also creates its website — the WebsiteSwitcher
      // reads from this key and must pick up the new entry immediately.
      queryClient.invalidateQueries({ queryKey: ['websites'] })
    },
  })
}

function useUpdateClientMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; isActive?: boolean; name?: string; contactEmail?: string | null; notes?: string | null }) =>
      api.patch(`/api/admin/clients/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

function useUpdateWebsiteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; url?: string }) =>
      api.patch(`/api/websites/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['websites'] })
    },
  })
}

function useDeleteClientMutation(onSuccess: () => void) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/clients/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      onSuccess()
    },
  })
}

function useInviteContactMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (email: string) =>
      api.post(`/api/admin/clients/${clientId}/invite`, { email }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients', clientId] }),
  })
}

// ─── Create client modal ────────────────────────────────────────

function CreateClientModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [websiteUrl, setWebsiteUrl] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const create = useCreateClientMutation()

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await create.mutateAsync({
        name,
        ...(email ? { contactEmail: email } : {}),
        ...(websiteUrl ? { websiteUrl } : {}),
        ...(notes ? { notes } : {}),
      })
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create client')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text">Add client</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="client-name">Company name *</Label>
            <Input id="client-name" required placeholder="Acme Corp" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client-website">Website URL</Label>
            <Input id="client-website" type="url" placeholder="https://acme.com" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client-email">Primary contact email</Label>
            <Input id="client-email" type="email" placeholder="contact@acme.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client-notes">Notes</Label>
            <textarea
              id="client-notes"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={3}
              placeholder="Internal notes about this client..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-feedback-error">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Adding…' : 'Add client'}</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Invite contact modal ───────────────────────────────────────

function InviteContactModal({ client, onClose }: { client: EndClient; onClose: () => void }) {
  const [email, setEmail] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const invite = useInviteContactMutation(client.id)

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await invite.mutateAsync(email)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to send invite')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-text">Invite contact</h3>
            <p className="text-sm text-text-muted mt-0.5">
              They'll get dashboard access scoped to {client.name}'s websites only.
            </p>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contact-email">Email address</Label>
            <Input id="contact-email" type="email" required placeholder="contact@acme.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {error && <p className="text-sm text-feedback-error">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={invite.isPending}>{invite.isPending ? 'Sending…' : 'Send invite'}</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Client detail panel ────────────────────────────────────────

function ClientDetail({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const { data, isLoading } = useClient(clientId)
  const update = useUpdateClientMutation()
  const updateWebsite = useUpdateWebsiteMutation()
  const deleteClient = useDeleteClientMutation(onClose)
  const [showInvite, setShowInvite] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)

  const [editName, setEditName] = React.useState('')
  const [editEmail, setEditEmail] = React.useState('')
  const [editNotes, setEditNotes] = React.useState('')
  const [editWebsiteUrls, setEditWebsiteUrls] = React.useState<Record<string, string>>({})

  function startEdit() {
    if (!data) return
    setEditName(data.name)
    setEditEmail(data.contactEmail ?? '')
    setEditNotes(data.notes ?? '')
    setEditWebsiteUrls(Object.fromEntries(data.websites.map((w) => [w.id, w.url])))
    setEditing(true)
  }

  async function handleSave() {
    if (!data) return
    const clientPatch: { id: string; name?: string; contactEmail?: string | null; notes?: string | null } = { id: data.id }
    if (editName !== data.name) clientPatch.name = editName
    if (editEmail !== (data.contactEmail ?? '')) clientPatch.contactEmail = editEmail || null
    if (editNotes !== (data.notes ?? '')) clientPatch.notes = editNotes || null

    const websitePatches = data.websites
      .filter((w) => editWebsiteUrls[w.id] !== undefined && editWebsiteUrls[w.id] !== w.url)
      .map((w) => ({ id: w.id, url: editWebsiteUrls[w.id] as string }))

    await Promise.all([
      Object.keys(clientPatch).length > 1 ? update.mutateAsync(clientPatch) : Promise.resolve(),
      ...websitePatches.map((p) => updateWebsite.mutateAsync(p)),
    ])
    setEditing(false)
  }

  const isSaving = update.isPending || updateWebsite.isPending

  if (isLoading) return <div className="flex items-center justify-center h-40"><Spinner /></div>
  if (!data) return null

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-text">Edit client</h3>
          <button onClick={() => setEditing(false)} className="text-text-muted hover:text-text"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="edit-name">Company name *</Label>
            <Input id="edit-name" required value={editName} onChange={(e) => setEditName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-email">Primary contact email</Label>
            <Input id="edit-email" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="contact@example.com" />
          </div>
          {data.websites.map((site) => (
            <div key={site.id} className="space-y-1.5">
              <Label htmlFor={`edit-url-${site.id}`}>
                {data.websites.length > 1 ? `Website URL — ${site.name}` : 'Website URL'}
              </Label>
              <Input
                id={`edit-url-${site.id}`}
                type="url"
                value={editWebsiteUrls[site.id] ?? ''}
                onChange={(e) => setEditWebsiteUrls((prev) => ({ ...prev, [site.id]: e.target.value }))}
              />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label htmlFor="edit-notes">Notes</Label>
            <textarea
              id="edit-notes"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={3}
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Internal notes..."
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
          <Button size="sm" disabled={isSaving || !editName.trim()} onClick={handleSave}>
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {showInvite && <InviteContactModal client={data} onClose={() => setShowInvite(false)} />}

      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowDeleteConfirm(false) }}
        >
          <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-xl">
            <h3 className="text-base font-semibold text-text mb-2">Remove client?</h3>
            <p className="text-sm text-text-muted mb-1">
              <strong className="text-text">{data.name}</strong> will be permanently deleted.
            </p>
            <p className="text-sm text-text-muted mb-4">
              Their linked websites will not be deleted — they'll become unassigned and all analysis data is preserved.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={deleteClient.isPending}
                onClick={() => deleteClient.mutate(data.id)}
              >
                {deleteClient.isPending ? 'Removing…' : 'Remove client'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-text">{data.name}</h3>
          {data.contactEmail && (
            <p className="text-sm text-text-muted flex items-center gap-1 mt-0.5">
              <Mail className="h-3 w-3" />{data.contactEmail}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={data.isActive ? 'default' : 'outline'}>{data.isActive ? 'Active' : 'Inactive'}</Badge>
          <Button variant="ghost" size="sm" onClick={startEdit}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" disabled={update.isPending} onClick={() => update.mutate({ id: data.id, isActive: !data.isActive })}>
            {data.isActive ? 'Deactivate' : 'Activate'}
          </Button>
          <button onClick={onClose} className="text-text-muted hover:text-text ml-1"><X className="h-4 w-4" /></button>
        </div>
      </div>

      {data.notes && (
        <p className="text-sm text-text-muted bg-surface-raised rounded-md px-3 py-2">{data.notes}</p>
      )}

      {data.websites.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-text mb-2">Websites ({data.websites.length})</h4>
          <div className="divide-y divide-border border border-border rounded-md">
            {data.websites.map((site) => (
              <div key={site.id} className="flex items-center gap-3 px-3 py-2.5">
                <Globe className="h-4 w-4 shrink-0 text-text-muted" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text truncate">{site.name}</p>
                  <a href={site.url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-primary hover:underline truncate block">{site.url}</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-text">Contacts ({data.contacts.length})</h4>
          <Button size="sm" variant="ghost" onClick={() => setShowInvite(true)}>
            <UserPlus className="h-3.5 w-3.5 mr-1" />Invite
          </Button>
        </div>
        {data.contacts.length === 0 ? (
          <p className="text-sm text-text-muted py-4 text-center border border-dashed border-border rounded-md">
            No contacts yet — invite someone to give them access.
          </p>
        ) : (
          <div className="divide-y divide-border border border-border rounded-md">
            {data.contacts.map((contact) => (
              <div key={contact.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-accent/20 text-xs font-medium text-brand-accent">
                  {(contact.email ?? '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-text">{contact.fullName ?? contact.email}</p>
                  {contact.fullName && <p className="text-xs text-text-muted">{contact.email}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          className="text-feedback-error hover:text-feedback-error hover:bg-feedback-error/10"
          onClick={() => setShowDeleteConfirm(true)}
        >
          Remove client
        </Button>
      </div>
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────────

export function ClientsPage() {
  const { data: clients, isLoading } = useClients()
  const [showCreate, setShowCreate] = React.useState(false)
  const [selectedId, setSelectedId] = React.useState<string | null>(null)

  if (isLoading) {
    return <div className="flex h-full items-center justify-center"><Spinner /></div>
  }

  const active = clients?.filter((c) => c.isActive) ?? []
  const inactive = clients?.filter((c) => !c.isActive) ?? []

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {showCreate && <CreateClientModal onClose={() => setShowCreate(false)} />}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text">Clients</h2>
          <p className="mt-1 text-sm text-text-muted">Manage your client accounts and their dashboard access.</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-1.5" />Add client
        </Button>
      </div>

      <div className={selectedId ? 'grid grid-cols-2 gap-6 items-start' : ''}>
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Active clients ({active.length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              {active.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center px-6">
                  <Building2 className="h-8 w-8 text-text-muted mb-3" />
                  <p className="text-sm text-text-muted">No clients yet.</p>
                  <p className="text-xs text-text-muted mt-1">Add your first client to give them access to their dashboard.</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {active.map((client) => (
                    <button
                      key={client.id}
                      onClick={() => setSelectedId(selectedId === client.id ? null : client.id)}
                      className={`w-full flex items-center gap-3 px-6 py-4 text-left transition-colors hover:bg-surface-raised ${selectedId === client.id ? 'bg-surface-raised' : ''}`}
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary font-semibold text-sm">
                        {client.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-text truncate">{client.name}</p>
                        {client.contactEmail && <p className="text-xs text-text-muted truncate">{client.contactEmail}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {inactive.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Inactive clients ({inactive.length})</CardTitle></CardHeader>
              <CardContent className="divide-y divide-border p-0">
                {inactive.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => setSelectedId(selectedId === client.id ? null : client.id)}
                    className="w-full flex items-center gap-3 px-6 py-4 text-left opacity-60 hover:opacity-100 transition-opacity hover:bg-surface-raised"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-raised text-text-muted font-semibold text-sm">
                      {client.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text truncate">{client.name}</p>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {selectedId && (
          <Card>
            <CardContent className="pt-6">
              <ClientDetail clientId={selectedId} onClose={() => setSelectedId(null)} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
